import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cardReveal, fadeUp, Reveal, staggerContainer, viewportOnce } from "../common/Motion";

const services = [
  ["AI strategy & readiness", "Find high-value use cases, assess data and risk, and shape an AI roadmap tied to measurable outcomes."],
  ["AI agents & copilots", "Build grounded assistants and agents that can retrieve knowledge, reason across context, and complete useful work."],
  ["Intelligent automation", "Connect AI to approvals, documents, customer conversations, and core systems with human oversight."],
  ["Data & knowledge systems", "Turn fragmented enterprise information into governed, searchable context that AI and people can trust."],
  ["AI-native products", "Design and engineer secure digital products where intelligence is part of the experience—not an afterthought."],
  ["Cloud, security & MLOps", "Operate models and applications with resilient infrastructure, evaluation, monitoring, and controlled access."],
];

const OurServices = () => {
  const reduceMotion = useReducedMotion();
  return (
  <section id="services" className="section services-section">
    <div className="site-shell">
      <Reveal className="section-heading split-heading">
        <div><p className="eyebrow">AI-first capabilities</p><h2>From promising idea to dependable intelligence.</h2></div>
        <p>We bring strategy, data, models, product design, and engineering into one delivery team—so AI moves beyond demos and becomes part of everyday work.</p>
      </Reveal>
      <motion.div className="service-grid" variants={reduceMotion ? undefined : staggerContainer} initial={reduceMotion ? false : "hidden"} whileInView="visible" viewport={viewportOnce}>
        {services.map(([title, copy], index) => <motion.article variants={cardReveal} whileHover={reduceMotion ? undefined : { y: -8, rotateX: 1.5, rotateY: index % 2 ? 1.25 : -1.25 }} className="service-card" key={title}><span className="service-number">0{index + 1}</span><h3>{title}</h3><p>{copy}</p><a href="#contact" aria-label={`Discuss ${title}`}>Learn more <span>↗</span></a></motion.article>)}
      </motion.div>
      <motion.article className="fbr-feature" variants={reduceMotion ? undefined : fadeUp} initial={reduceMotion ? false : "hidden"} whileInView="visible" viewport={viewportOnce}>
        <div className="fbr-brand">
          <p className="fbr-overline">A Visiosol product</p>
          <a href="https://fbrconnect.visiosol.tech" aria-label="Visit the FBRConnect website">
            <img src="/logo-fbrconnect.png" alt="FBRConnect" width="982" height="239" loading="lazy" />
          </a>
          <p>Digital invoicing integration designed for businesses operating in Pakistan.</p>
        </div>
        <div className="fbr-content">
          <p className="eyebrow">Compliance, connected</p>
          <h3>FBR integration without the operational friction.</h3>
          <p>Connect your sales and finance systems with FBR workflows through one dependable integration layer. Automate invoice submission, validate data before sending, and track every transaction with confidence.</p>
          <ul><li>Automated invoice submission</li><li>Validation and error visibility</li><li>Secure, traceable integration</li></ul>
          <div className="fbr-actions">
            <a className="button button-primary" href="https://fbrconnect.visiosol.tech">Explore FBRConnect <span>↗</span></a>
            <a className="button button-secondary" href="#contact">Talk to our team</a>
          </div>
        </div>
        <figure className="fbr-testimonial">
          <img src="/riaz-ahmed-amir.png" alt="Riaz Ahmed Amir" width="320" height="320" loading="lazy" />
          <div>
            <blockquote>“FBRConnect makes digital invoicing simple, accurate, and efficient for businesses managing their FBR compliance.”</blockquote>
            <figcaption>
              <strong>Riaz Ahmed Amir</strong>
              <span>Group Director Taxation, Jang Group of Companies</span>
            </figcaption>
          </div>
        </figure>
        <div className="fbr-onprem">
          <div className="onprem-icon" aria-hidden="true"><span></span><span></span><span></span></div>
          <div className="onprem-copy">
            <p className="onprem-label">On-premises deployment available</p>
            <h4>Your infrastructure. Your data. Your control.</h4>
            <p>Deploy FBRConnect within your own environment and keep sensitive invoice and business data inside your organization’s infrastructure. Your records are processed under your governance policies—without being stored in Visiosol’s cloud or a third-party database.</p>
          </div>
          <div className="onprem-benefits">
            <div><strong>Data sovereignty</strong><span>Business data remains within your controlled environment.</span></div>
            <div><strong>Governance aligned</strong><span>Apply your own access, retention, backup, and security policies.</span></div>
            <div><strong>Direct integration</strong><span>Connect securely with your ERP, POS, or finance systems.</span></div>
          </div>
          <a className="onprem-link" href="#contact">Discuss an on-premises setup <span>→</span></a>
        </div>
      </motion.article>
    </div>
  </section>
  );
};
export default OurServices;
